import { type Message, strings, text } from "@quickpoint.me/shared";
import { createContext, useEffect, useRef, useState } from "react";
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from "recoil";
import SettingsIcon from "./icons/settings.svg?react";
import { CurrentFilter, FilterFonts } from "./state/filter";
import {
	AutoPlayAtom,
	ConstrainDimensionsSelector,
	DebugMode,
	HostId,
	IsInAuthor,
	IsInPopOut,
	IsInPresentationMode,
	IsPreviewGenerator,
	ShowInfoBarAtom,
	ShowSettingsAtom,
	TapDirection,
	TapEvent,
} from "./state/ui";
import { HideAttribution, NumberOfPages } from "./state/selectors";

import styled, { ThemeProvider } from "styled-components";
import StyledDisplay from "./StyledDisplay";
import KeyboardHelp from "./displayComponents/KeyboardHelp";
import DisplayElements from "./displayComponents/displayElements/DisplayElements";
import InfoBar from "./displayComponents/displayElements/InfoBar";
import Settings from "./displayComponents/displayElements/Settings";
import FilterBackGround from "./displayComponents/pageElements/FilterBackGround";
import StoryBackGround from "./displayComponents/pageElements/StoryBackGround";
// import useTapNavigationHandlerHook from "./lib/animations/tapNavigationHandlerHook";
import useReaderSettings from "./lib/useReaderSettings";
import { NeueroDivergentReader } from "./state/accessibility";
import { NavigationType } from "./state/navigation";
import { LastRemotePageAtom, PageIndex, PageSizeAtom } from "./state/page";
import { sendGoToPageToAuthor } from "./functionalComponents/MessageHandler";

export const ElementsTimeline = createContext<GSAPTimeline | undefined>(
	undefined,
);

/* visually hidden but exposed to assistive tech — announces slide changes */
const SrOnlyLiveRegion = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const NavButton = styled.button<{ $isNDR: boolean; $isInAuthor?: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  /* author mode: leave the middle free so element taps reach click-to-edit */
  width: ${(props) => (props.$isInAuthor ? "15%" : "50%")};
  height: 100%;
  z-index: 1; /* Ensure buttons are on top of other content */
  /* Visually hide the button but keep it accessible */
  background: transparent;
  border: none;
  color: transparent;
  padding: 0;
  margin: 0;
  cursor: pointer;
  outline: none;

  &:focus-visible {
    /* keyboard users need to see where focus went — these buttons are
       otherwise invisible half-screen overlays (WCAG 2.4.7) */
    outline: 3px solid #4a90d9;
    outline-offset: -6px;
    border-radius: 8px;
  }
`;

const LeftNavButton = styled(NavButton)`
  left: 0;
`;

const RightNavButton = styled(NavButton)`
  right: 0;
`;

/* attribution badge, bottom-left opposite the settings button. AAA: real
   link text at ≥7:1 on its pill, 44px min target via padding */
const MadeWithBadge = styled.a`
  position: fixed;
  bottom: 16px;
  left: 16px;
  z-index: 10;
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: 0 14px;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid #767676;
  color: #333;
  font-family: Avantt-Regular, sans-serif;
  font-size: 0.8rem;
  text-decoration: none;

  &:hover,
  &:focus-visible {
    color: #000;
    border-color: #333;
  }
`;

const SettingsButton = styled.button`
  position: fixed;
  bottom: 16px;
  right: 16px;
  z-index: 10;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  transition: background 0.2s ease;
  z-index: 103;

  &:hover {
    background: rgba(0, 0, 0, 0.8);
  }

  &:focus-visible {
    outline: 3px solid #4A90D9;
    outline-offset: 2px;
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

function Display({ container }: { container: HTMLElement | null }) {
	const hostId = useRecoilValue(HostId);
	const constrainDimensions = useRecoilValue(ConstrainDimensionsSelector);
	const fonts = useRecoilValue(FilterFonts);
	const isPreviewer = useRecoilValue(IsPreviewGenerator);
	const isInAuthor = useRecoilValue(IsInAuthor);
	const pageSize = useRecoilValue(PageSizeAtom);
	const baseFontSize = pageSize.baseFontSize;
	const showSettings = useRecoilValue(ShowSettingsAtom);
	const showInfoBar = useRecoilValue(ShowInfoBarAtom);
	const filter = useRecoilValue(CurrentFilter);
	const viewerRef = useRef<HTMLDivElement>(null);
	const navigationType = useRecoilValue(NavigationType);
	const debugMode = useRecoilValue(DebugMode);
	const leftNavRef = useRef<HTMLButtonElement>(null);
	const rightNavRef = useRef<HTMLButtonElement>(null);
	const isInPresentationMode = useRecoilValue(IsInPresentationMode);
	const isInPopOut = useRecoilValue(IsInPopOut);
	const hideAttribution = useRecoilValue(HideAttribution);
	/* the pop-out is the accessible preview: even though it runs inAuthor
	   (live-synced), it must be independently navigable and expose the
	   accessibility settings */
	const chromeEnabled = !isInAuthor || isInPopOut;
	const pageIndex = useRecoilValue(PageIndex);
	const isNDR = useRecoilValue(NeueroDivergentReader);
	const setShowSettings = useSetRecoilState(ShowSettingsAtom);
	const numberOfPages = useRecoilValue(NumberOfPages);
	const [showShortcuts, setShowShortcuts] = useState(false);
	/* announce slide changes to screen readers; empty until the first page
	   turn so the initial load isn't narrated over the page content */
	const [slideAnnouncement, setSlideAnnouncement] = useState("");
	const announcedPageRef = useRef<number | null>(null);
	useEffect(() => {
		if (announcedPageRef.current === null) {
			announcedPageRef.current = pageIndex;
			return;
		}
		if (announcedPageRef.current === pageIndex) return;
		announcedPageRef.current = pageIndex;
		// PageIndex is 1-based (parsed from the URL)
		setSlideAnnouncement(`Slide ${pageIndex} of ${numberOfPages}`);
	}, [pageIndex, numberOfPages]);

	if (debugMode) console.info("debugMode", debugMode);

	useReaderSettings();
	// useTapNavigationHandlerHook(viewerRef);

	useEffect(() => {
		if (navigationType === "tap" && viewerRef.current) {
			viewerRef.current.scrollTo({ top: 0 });
		}
	}, [pageIndex, navigationType]);

	useEffect(() => {
		const toAuthor = (() => {
			try {
				const bc = new BroadcastChannel(strings.broadcastChannel.author);
				return bc;
			} catch {
				return false;
			}
		})();

		if (container) {
			if (toAuthor && isInAuthor) {
				const message: Message = {
					hostId,
					type: isPreviewer ? "preview" : "QP",
					action: strings.messages.requestStack,
				};
				toAuthor.postMessage(message);
			}
		}

		return () => {
			if (toAuthor) toAuthor.close();
		};
	}, [isInAuthor, isPreviewer, hostId, container]);

	const handleBackClick = useRecoilCallback(({ set, snapshot }) => () => {
		const pageIndex = snapshot.getLoadable(PageIndex).getValue();
		if (navigationType !== "tap" || pageIndex <= 1) return;
		set(TapEvent, "back");
		set(TapDirection, "back");
		set(AutoPlayAtom, { autoPlay: false, loop: "once" });
	});

	const handleForwardClick = useRecoilCallback(({ set }) => () => {
		if (navigationType !== "tap") return;
		set(TapEvent, "forward");
		set(TapDirection, "forward");
		set(AutoPlayAtom, { autoPlay: false, loop: "once" });
	});

	/* Home/End jump straight to the first/last page (the presentation-viewer
	   convention); direction is set so the tap containers orient correctly */
	const handleJump = useRecoilCallback(
		({ set, snapshot }) =>
			(toEnd: boolean) => {
				if (navigationType !== "tap") return;
				const count = snapshot.getLoadable(NumberOfPages).getValue();
				set(TapDirection, toEnd ? "forward" : "back");
				set(PageIndex, toEnd ? count : 1);
				set(AutoPlayAtom, { autoPlay: false, loop: "once" });
			},
	);
	useEffect(() => {
		const onKeyDown = (e: KeyboardEvent) => {
			if (
				(isInAuthor && !isInPresentationMode && !isInPopOut) ||
				navigationType !== "tap"
			)
				return;

			const target = e.target as HTMLElement | null;
			const tag = target?.tagName?.toLowerCase();
			const isEditable =
				!!target &&
				(target.isContentEditable ||
					tag === "input" ||
					tag === "textarea" ||
					tag === "select");

			if (isEditable) return;

			/* Space/Enter on a focused control activates the control — the
			   global page-turn must not also fire (double action) */
			const onControl = !!target?.closest("button, a, [role=button]");

			// the conventional presentation-viewer keys (Google Slides,
			// PowerPoint, reveal.js): arrows, space, page keys, home/end
			switch (e.key) {
				case "ArrowLeft":
				case "PageUp":
					e.preventDefault();
					handleBackClick();
					break;
				case "ArrowRight":
				case "PageDown":
					e.preventDefault();
					handleForwardClick();
					break;
				case " ":
					if (onControl) return;
					e.preventDefault();
					if (e.shiftKey) handleBackClick();
					else handleForwardClick();
					break;
				case "Home":
					e.preventDefault();
					handleJump(false);
					break;
				case "End":
					e.preventDefault();
					handleJump(true);
					break;
				case "?":
					e.preventDefault();
					setShowShortcuts((v) => !v);
					break;
			}
		};

		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [
		isInAuthor,
		isInPopOut,
		navigationType,
		handleBackClick,
		handleForwardClick,
		handleJump,
		isInPresentationMode,
	]);

	// two-way page tie: a page turned locally in the pop-out moves the author
	// too. Remote goToPage messages record LastRemotePageAtom first, so this
	// only fires for turns the user made here — no echo loop.
	const lastRemotePage = useRecoilValue(LastRemotePageAtom);
	useEffect(() => {
		if (!isInAuthor || !isInPopOut) return;
		if (pageIndex === lastRemotePage) return;
		sendGoToPageToAuthor("QP", pageIndex);
	}, [pageIndex, lastRemotePage, isInAuthor, isInPopOut]);

	return (
		<ThemeProvider theme={filter}>
			<StyledDisplay
				/* the standalone viewer (and pop-out) is the page's main content;
				   embedded author previews must not add a second main landmark */
				as={chromeEnabled ? "main" : undefined}
				data-testid="styled-display"
				id="styled-display"
				className={strings.css.viewerContainer}
				$fonts={fonts}
				$constrainDimensions={constrainDimensions}
				$isPreviewer={isPreviewer}
				$baseFontSize={baseFontSize}
				$baseFontSizeRaw={pageSize.baseFontSizeRaw}
				$isInLandscape={pageSize.landscape}
				$forceSingleColumn={pageSize.forceSingleColumn}
				$height={pageSize.height}
				$width={pageSize.width}
				$navigationType={navigationType}
				ref={viewerRef}
			>
				{chromeEnabled && (
					<SrOnlyLiveRegion aria-live="polite" role="status">
						{slideAnnouncement}
					</SrOnlyLiveRegion>
				)}
				<FilterBackGround />
				<StoryBackGround leftOrTop={true} />
				<StoryBackGround leftOrTop={false} />
				<DisplayElements />
				{chromeEnabled && navigationType === "tap" && (
					<>
						{/* Next before Previous in DOM: after reading the slide the
						    very next stop is the most common action (visual
						    positions are absolute, so order here is free) */}
						<RightNavButton
							ref={rightNavRef}
							onClick={handleForwardClick}
							aria-label="Next Page"
							aria-keyshortcuts="ArrowRight"
							$isNDR={isNDR}
							$isInAuthor={isInAuthor}
						/>
						<LeftNavButton
							ref={leftNavRef}
							onClick={handleBackClick}
							aria-label="Previous Page"
							aria-keyshortcuts="ArrowLeft"
							$isNDR={isNDR}
							$isInAuthor={isInAuthor}
						/>
					</>
				)}
				{chromeEnabled && (
					<SettingsButton
						onClick={() => setShowSettings(true)}
						aria-label="Open accessibility settings"
						type="button"
					>
						<SettingsIcon aria-hidden="true" />
					</SettingsButton>
				)}
				{/* attribution on published stories — free distribution loop.
				    Plus owners publish without it (hideAttribution resolves
				    live from their tier). Author contexts never show it. */}
				{!isInAuthor && !isPreviewer && !hideAttribution && (
					<MadeWithBadge
						href="https://quickpoint.me/?utm_source=viewer&utm_medium=attribution&utm_campaign=made_with"
						target="_blank"
						rel="noopener"
					>
						{text.reader.madeWith} — {text.reader.madeWithCta}
					</MadeWithBadge>
				)}
			</StyledDisplay>

			{showSettings && <Settings />}
			{showShortcuts && chromeEnabled && (
				<KeyboardHelp onClose={() => setShowShortcuts(false)} />
			)}
			{showInfoBar && <InfoBar />}
		</ThemeProvider>
	);
}

export default Display;
